import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ShoppingBag, List, X, SignOut, Crown } from "@phosphor-icons/react";
import { useState } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import Marquee from "react-fast-marquee";

const NAV = [
  { to: "/comics", label: "Comics" },
  { to: "/shop", label: "Shop" },
  { to: "/blog", label: "Blog" },
  { to: "/videos", label: "Videos" },
  { to: "/chat", label: "Live Chat" },
  { to: "/about", label: "About" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = () => { logout(); nav("/"); };

  return (
    <div className="min-h-screen flex flex-col" data-testid="layout-root">
      <div className="bg-[#FF3B30] text-black border-b border-black/20" data-testid="top-marquee">
        <Marquee speed={50} gradient={false} className="py-1.5 font-display tracking-[0.25em] text-xs">
          <span className="mx-8">SAVAGE COMIX UNIVERSE</span>
          <span className="mx-8">★</span>
          <span className="mx-8">THE SAVAGE KING LEGACY · ISSUE #001 OUT OCTOBER 6, 2026</span>
          <span className="mx-8">★</span>
          <span className="mx-8">LONG LIVE THE KING</span>
          <span className="mx-8">★</span>
          <span className="mx-8">JOIN THE SCU</span>
          <span className="mx-8">★</span>
        </Marquee>
      </div>

      <header className="sticky top-0 z-40 bg-[#0A0A0A] border-b border-white/10" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-9 h-9 bg-[#FF3B30] flex items-center justify-center font-display text-black text-xl">S</div>
            <div className="leading-none">
              <div className="font-display text-xl tracking-wider">SCU</div>
              <div className="text-[10px] text-dim tracking-[0.3em] uppercase">Savage ComiX Universe</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `font-display tracking-[0.15em] text-sm uppercase transition-colors ${isActive ? "text-[#FF3B30]" : "text-white hover:text-[#FF3B30]"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user && (
              <Link to="/admin" className="hidden lg:inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FFD700] transition-colors" data-testid="admin-link">
                <Crown size={16} weight="fill" className="text-[#FFD700]" />
                <span className="font-display text-sm tracking-widest">ADMIN</span>
              </Link>
            )}
            {user ? (
              <button onClick={doLogout} className="hidden lg:inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FF3B30] transition-colors" data-testid="logout-btn">
                <SignOut size={16} />
                <span className="font-display text-sm tracking-widest">OUT</span>
              </button>
            ) : null}
            <Link to="/cart" data-testid="cart-link" className="relative px-3 py-2 border border-white/15 hover:border-[#FF3B30] transition-colors inline-flex items-center gap-2">
              <ShoppingBag size={18} weight="bold" />
              <span className="font-display text-sm tracking-widest">CART</span>
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF3B30] text-black font-display text-xs w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>
            <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="menu">
              {open ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#0A0A0A]" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => `font-display tracking-widest text-base ${isActive ? "text-[#FF3B30]" : "text-white"}`}>
                  {n.label}
                </NavLink>
              ))}
              {user && (
                <Link to="/admin" onClick={() => setOpen(false)} className="font-display tracking-widest text-base text-[#FFD700]" data-testid="mobile-nav-admin">Admin</Link>
              )}
              {user ? (
                <button onClick={() => { setOpen(false); doLogout(); }} className="font-display tracking-widest text-base text-left text-white" data-testid="mobile-logout">Sign Out</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="font-display tracking-widest text-base text-white" data-testid="mobile-nav-login">Sign In</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="main-content">
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] mt-20" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="font-display text-3xl tracking-wider">SAVAGE COMIX UNIVERSE</div>
            <p className="mt-3 text-dim max-w-md">
              Built in the ring. Drawn in the dark. The SCU is the home of The Savage King and the comics, fights, and stories that shaped him.
            </p>
          </div>
          <div>
            <div className="section-eyebrow">Explore</div>
            <ul className="mt-4 space-y-2">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-[#FF3B30] transition-colors">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="section-eyebrow">The King</div>
            <ul className="mt-4 space-y-2 text-dim">
              <li>
                <a href="mailto:Info@savagecomixuniverse.com" className="hover:text-[#FF3B30] transition-colors break-all" data-testid="footer-email">
                  Info@savagecomixuniverse.com
                </a>
              </li>
              <li>
                <a href="https://linktr.ee/thesavageking" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF3B30] transition-colors" data-testid="footer-linktree">
                  linktr.ee/thesavageking
                </a>
              </li>
              <li>
                © {new Date().getFullYear()} SCU ·{" "}
                <Link
                  to="/the-throne"
                  className="text-dim hover:text-[#FFD700] transition-colors cursor-default"
                  aria-label="Hidden lore"
                  data-testid="easter-egg-trigger"
                >
                  All hail the crown.
                </Link>
              </li>
              <li>{!user && <Link to="/login" className="hover:text-[#FF3B30] underline" data-testid="footer-login-link">Admin sign in</Link>}</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
